/** @format */

import React from "react";
import styled from "@emotion/styled";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { NavLink, Routes, Route } from "react-router-dom";
import MaintenancePage from "./components/MaintenancePage";
import Home from "./routes/Home";
import About from "./routes/About";
import Services from "./routes/Services";
import Projects from "./routes/Projects";
import Contact from "./routes/Contact";
import Company from "./routes/Company";
import Team from "./routes/Team";
import Hotels from "./routes/Hotels";
import Apartments from "./routes/Apartments";
import RetailHealth from "./routes/RetailHealth";
import InvestmentOpportunities from "./routes/InvestmentOpportunities";
import pdgLogo from "./assets/PDG_Logo-removebg-preview.png";

const Nav = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  position: fixed;
  top: 0;
  width: 100%;
  height: auto;
  min-height: 100px;
  z-index: 50;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  box-sizing: border-box;

  @media (min-width: 480px) {
    padding: 0.75rem;
    min-height: 40px;
  }

  @media (min-width: 768px) {
    padding: 1rem 2rem;
    min-height: 50px;
  }

  @media (min-width: 1024px) {
    padding: 1rem 4rem;
    min-height: 60px;
  }
`;

const Logo = styled.img`
  height: 150px;
  position: absolute;
  left: 12%;
  transform: translateX(-50%);
  top: 100%;
  margin-top: -40px;

  @media (min-width: 768px) {
    height: 150px;
    margin-top: -60px;
  }

  @media (min-width: 1024px) {
    height: 150px;
    margin-top: -60px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    gap: 2.5rem;
  }
  a {
    color: #000;
    text-decoration: none;
    font-family: "calibri";
    font-size: 1rem;
    position: relative;
    transition: color 0.3s ease;
    padding: 10px 0;

    @media (min-width: 768px) {
      font-size: 1.1rem;
    }

    @media (min-width: 1024px) {
      font-size: 1.2rem;
    }

    &:after {
      content: "";
      position: absolute;
      width: 0;
      height: 2px;
      bottom: -4px;
      left: 0;
      background-color: #0066cc;
      transition: width 0.3s ease;
    }

    &:hover,
    &.active {
      color: #0066cc;
      &:after {
        width: 100%;
      }
    }
  }

  .dropdown-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 0%;
    background-color: white;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 15px 20px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px) translateX(-25%);
    transition: all 0.3s ease;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    text-align: center;
    z-index: 1000;

    @media (min-width: 768px) {
      transform: translateY(-10px) translateX(-30%);
    }
  }

  .dropdown-container:not(:has(> a[href="/services"])):hover .dropdown {
    opacity: 1;
    visibility: visible;
    transform: translateY(0) translateX(-30%);
  }
`;

const DropdownItem = styled(NavLink)`
  padding: 0.75rem 1.5rem;
  color: #666;
  text-decoration: none;
  font-family: "calibri";
  font-size: 1.1rem;
  display: block;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const HeroSection = styled.div`
  height: 100vh;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url("/home BG.jpg");
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 1rem;
  color: white;

  @media (min-width: 768px) {
    padding: 0 4rem;
  }
`;

const Title = styled.h1`
  font-family: century gothic;
  font-size: 1.5rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    font-size: 6.4rem;
  }

  span {
    color: #0066cc;
  }
`;

const Subtitle = styled.h2`
  font-family: "century gothic";
  font-size: 1.2rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }

  @media (min-width: 1024px) {
    font-size: 3rem;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15rem;
  margin-top: 4rem;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 15rem;
    margin-top: 8rem;
  }
`;

const StatItem = styled(motion.div)`
  font-size: 2rem;
  font-family: "century gothic";
  text-align: center;

  @media (min-width: 768px) {
    font-size: 3rem;
    text-align: left;
  }
`;

const Footer = styled.footer`
  background-color: #333;
  margin-top: 0; // Change this from 20% to 0
  width: 100%;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1800px;
  margin: 40px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;

  h3 {
    color: #0066cc;
    margin-bottom: 1rem;
    font-family: "century gothic";
  }

  p,
  a {
    margin: 0.5rem 0;
    margin-right: 12rem;
    color: #fff;
    text-decoration: none;
    font-family: "calibri";
    transition: color 0.3s ease;
  }

  a:hover {
    color: #0066cc;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-family: "calibri";
  font-size: 0.9rem;
  color: #999;
`;

const AnimatedCounter = ({ target, title }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 },
      });
    }
  }, [controls, inView]);

  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (inView) {
      const duration = 2000;
      const steps = 60;
      const stepValue = target / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        if (currentStep < steps) {
          setCount(Math.round(stepValue * currentStep));
          currentStep++;
        } else {
          setCount(target);
          clearInterval(timer);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [inView, target]);

  return (
    <StatItem ref={ref} initial={{ opacity: 0, y: 20 }} animate={controls}>
      {count.toLocaleString()}
      <div style={{ fontSize: "2rem", marginTop: "0.5rem" }}>{title}</div>
    </StatItem>
  );
};

// Add this constant at the top of your file
const MAINTENANCE_MODE = true; // Set to false to disable maintenance mode

const App = () => {
  // If in maintenance mode, show maintenance page
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}>
      <Nav>
        <NavLink to='/'>
          <Logo src={pdgLogo} alt='PDG Logo' />
        </NavLink>
        <NavLinks>
          <NavLink
            to='/'
            className={({ isActive }) => (isActive ? "active" : "")}>
            Home
          </NavLink>

          <div className='dropdown-container'>
            <NavLink
              to='/about'
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={(e) => e.preventDefault()}>
              About
            </NavLink>
            <div className='dropdown'>
              <DropdownItem to='/company'>Company</DropdownItem>
              <DropdownItem to='/about/team'>Team</DropdownItem>
            </div>
          </div>

          <div className='dropdown-container'>
            <NavLink
              to='/services'
              className={({ isActive }) => (isActive ? "active" : "")}>
              Services
            </NavLink>
            <div className='dropdown'>
              <DropdownItem to='/services/pre-development'>
                Pre-Development & Entitlements
              </DropdownItem>
              <DropdownItem to='/services/planning'>
                Planning, Design & Construction
              </DropdownItem>
              <DropdownItem to='/services/redevelopment'>
                Redevelopment
              </DropdownItem>
              <DropdownItem to='/services/financing'>Financing</DropdownItem>
            </div>
          </div>

          <div className='dropdown-container'>
            <NavLink
              to='/projects'
              className={({ isActive }) => (isActive ? "active" : "")}>
              Projects
            </NavLink>
            <div className='dropdown'>
              <DropdownItem to='/projects/hotels'>Hotels</DropdownItem>
              <DropdownItem to='/projects/apartments'>Apartments</DropdownItem>
              <DropdownItem to='/projects/retail-healthcare'>
                Retail & Healthcare
              </DropdownItem>
            </div>
          </div>
          <NavLink
            to='/investment-opportunities'
            className={({ isActive }) => (isActive ? "active" : "")}>
            Investment Opportunities
          </NavLink>
          <NavLink
            to='/contact'
            className={({ isActive }) => (isActive ? "active" : "")}>
            Contact
          </NavLink>
        </NavLinks>
      </Nav>
      <Routes>
        <Route path='/' element={<Home />} />
        <Route path='/about' element={<About />} />
        <Route path='/services' element={<Services />} />
        <Route path='/projects' element={<Projects />} />
        <Route path='/contact' element={<Contact />} />
        <Route path='/company' element={<Company />} />
        <Route path='/about/team' element={<Team />} />
        <Route path='/projects/hotels' element={<Hotels />} />
        <Route path='/projects/apartments' element={<Apartments />} />
        <Route path='/projects/retail-healthcare' element={<RetailHealth />} />
        <Route
          path='/investment-opportunities'
          element={<InvestmentOpportunities />}
        />
      </Routes>
      <Footer>
        <FooterContent>
          <div>
            <h3>About PDG</h3>
            <p>
              PDG is a premier commercial real estate developer with 50+ years
              of leadership experience, specializing in hotels, healthcare,
              apartments, and mixed-use properties. We emphasize collaboration,
              risk mitigation, and top-tier quality to deliver exceptional
              results for partners and investors.
            </p>
          </div>
          <div>
            <h3>Quick Links</h3>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <NavLink to='/'>Home</NavLink>
              <NavLink to='/about'>About</NavLink>
              <NavLink to='/services'>Services</NavLink>
              <NavLink to='/projects'>Projects</NavLink>
              <NavLink to='/contact'>Contact</NavLink>
            </div>
          </div>
          <div>
            <h3>Contact Us</h3>
            <p>Email: info@pateldevelopmentgroup.com</p>
            <p>Address: 2850 Lake Vista Dr. Suite 140 Lewisville, TX 75067</p>
          </div>
        </FooterContent>
        <Copyright>
          <p>
            © {new Date().getFullYear()} Patel Development Group. All rights
            reserved.
          </p>
        </Copyright>
      </Footer>
    </div>
  );
};

export default App;
